import { css } from '@emotion/react';
import { Formik, useField } from 'formik';

import { ApiOrgProperty } from '@kaiizen/kai-pro-api';

import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { usePropertyIdLabel } from '../../Lib';
import { useOrgMarkets, useOrgProperties, useToast } from '../../State';
import { Button, FormikLabeledInput, FormikLabeledSelect } from '../../Widgets';

const CustomFormikSelect = (
  { name, label, items, ...props }: { name: string; label: string; items: { value: string; label: string }[]; [key: string]: any },
) => {
  const [field, meta] = useField(name);

  return (
    <FormControl fullWidth {...props}>
      <InputLabel>{label}</InputLabel>
      <Select
        {...field}
        value={field.value || ''}
        onChange={field.onChange}
        label={label}
        error={meta.touched && !!meta.error}
      >
        <MenuItem value=''>
          <em>Select a market (optional)</em>
        </MenuItem>
        {items.map(item => (
          <MenuItem key={item.value} value={item.value}>
            {item.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

interface PropertyEditPanelProps {
  property: ApiOrgProperty;
  onClose: () => void;
  readOnly?: boolean;
}

export const PropertyEditPanel = ({ property, onClose, readOnly }: PropertyEditPanelProps) => {
  const { propertiesMutators } = useOrgProperties();
  const { markets } = useOrgMarkets();
  const { showToast, showErrorToast } = useToast();
  const propertyIdLabel = usePropertyIdLabel();

  const updateProperty = async (idNumber: string, orgMarketId?: string) => {
    await propertiesMutators.update(property.id, {
      address: property.address, // Keep existing address
      customerMetadata: {
        propertyId: String(idNumber).trim() || null,
        zipCode: property.customerMetadata?.zipCode || null, // Keep existing zip code
      },
      orgMarketId: orgMarketId || null,
    });
    onClose();
  };

  return (
    <Formik<{ address: string; idNumber: string; orgMarketId: string }>
      initialValues={{
        address: property.address,
        idNumber: property.customerMetadata?.propertyId?.toString() || '',
        orgMarketId: property.orgMarketId || '',
      }}
      enableReinitialize
      onSubmit={async values => {
        try {
          await updateProperty(values.idNumber, values.orgMarketId);
          showToast('Property updated successfully', 'success');
        } catch (error) {
          showErrorToast(error, 'update a property');
        }
      }}
    >
      {({ handleSubmit }) => (
        <form
          onSubmit={handleSubmit}
          css={css`
          display: flex;
          flex-direction: column;
          gap: 20px;
        `}
        >
          <FormikLabeledInput
            name='address'
            autoComplete='street-address'
            label='Street Address'
            width='100%'
            disabled
          />

          <FormikLabeledInput
            name='idNumber'
            label={propertyIdLabel}
            placeholder='Enter the ID number'
            disabled={readOnly}
          />

          <FormikLabeledSelect
            name='orgMarketId'
            label='Market'
            items={markets ? markets.map(m => ({ value: m.id, label: m.name })) : []}
            disabled={readOnly}
          />

          {!readOnly && (
            <Button
              color='green'
              type='submit'
            >
              Update Property
            </Button>
          )}
        </form>
      )}
    </Formik>
  );
};
